/**
 * Отправка заказа QR-папки фабрике в WeChat — состояние групп «Планов».
 *
 * Спека: `docs/specs/2026-09-28-wechat-send-design.md`. Команду исполняет
 * `wechat-api` соседа за 1–5 минут, поэтому кнопка только ставит её, а исход
 * экран узнаёт опросом `GET /api/plans/wechat` — пока хоть одна команда идёт.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import { apiFetch } from "@/lib/api";
import type { Mark } from "@/lib/plans";

export type WechatState = {
  qr_id: number;
  key: string;
  status: string;
  error: string | null;
  command_id: number | null;
  /** Непусто — тестовый режим: ушло в эту группу, а не поставщику. */
  chat: string | null;
  finished_at: string | null;
  user_id: number | null;
  /** Сколько сообщений о недостаче ушло по заказам папки (только в ответе «Планов»). */
  shortage?: number;
  /** Техкарты вслед за заказом (спека 2026-10-28-wechat-techcard-followup): пачки по 5. */
  techcard?: TechcardState;
};

export type TechcardState = {
  /** Пачек всего и отправленных — пачка до 5 картинок. */
  total: number;
  sent: number;
  /** `none` — техкарт нет или заказ ушёл до выката: метки нет. */
  status: "none" | "held" | "sending" | "sent" | "error";
  error: string | null;
};

/**
 * Техкарты ещё могут сдвинуться без клика: идут, либо ждут заказа, который
 * отправлен или отправляется. Ждущие при НЕотправленном заказе так и лежат до
 * ↻ — опрашивать их незачем.
 */
export function isTechcardPending(state: WechatState | undefined): boolean {
  const tc = state?.techcard;
  if (!state || !tc) return false;
  if (tc.status === "sending") return true;
  return tc.status === "held" && (state.status === "sent" || isBusy(state));
}

/** Опрос раз в 15 с — так советует контракт wechat-api (10–30 с). */
export const POLL_MS = 15_000;

// Как на сервере: `new` — строка журнала, до двери не дошедшая, это не «идёт».
const BUSY = new Set(["queued", "running"]);

export function isBusy(state: WechatState | undefined): boolean {
  return state !== undefined && BUSY.has(state.status);
}

function when(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const p = (n: number) => String(n).padStart(2, "0");
  return ` ${p(d.getDate())}.${p(d.getMonth() + 1)} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** Метка группы словами; `null` — ещё не отправляли. */
export function wechatMark(state: WechatState | undefined): Mark | null {
  if (!state) return null;
  const куда = state.chat ? `в тестовую группу ${state.chat}` : "поставщику";
  const тест = state.chat ? " · тест" : "";
  switch (state.status) {
    case "sent":
      return {
        text: `WeChat${when(state.finished_at)}${тест}`,
        tone: "ok",
        title: `заказ отправлен ${куда} и увиден в чате (команда ${state.command_id})`,
      };
    case "unverified":
      return {
        text: "WeChat: не подтверждено",
        tone: "err",
        title:
          `отправку начали, но в чате её не увидели — сообщение МОГЛО уйти ${куда}. ` +
          "Проверьте чат, прежде чем отправлять ещё раз.",
      };
    case "failed":
    case "refused":
    case "new":
      return {
        text: "WeChat: не отправлено",
        tone: "err",
        title: `не отправлено ${куда}: ${state.error ?? "причина не названа"}`,
      };
    default:
      return {
        text: "WeChat: отправляется…",
        tone: "muted",
        title: `в очереди wechat-api ${куда}${state.error ? ` — ${state.error}` : ""}`,
      };
  }
}

function techcardBatches(tc: TechcardState): string {
  // Пачки, а не картинки: «1 из 2» в строке читалось бы как одна техкарта из двух.
  if (Number.isInteger(tc.total) && Number.isInteger(tc.sent) && tc.total > 0 && tc.sent >= 0 && tc.sent <= tc.total)
    return `Отправлено пачек: ${tc.sent} из ${tc.total}. В пачке до 5 картинок.`;
  return "Количество пачек не указано.";
}

/**
 * Метка техкарт рядом с меткой заказа (макет Codex
 * `docs/design/2026-10-28-wechat-techcard`): без дроби в строке, подробности —
 * в подсказке. Метка заказа от неё не меняется — заказ правда ушёл. `null` —
 * техкарт нет или заказ ушёл до выката.
 */
export function techcardMark(state: WechatState | undefined): Mark | null {
  const tc = state?.techcard;
  if (!state || !tc || tc.status === "none") return null;
  const куда = state.chat ? `в тестовую группу ${state.chat}` : "поставщику";
  const пачки = techcardBatches(tc);
  switch (tc.status) {
    case "held": {
      let title =
        `Техкарты собраны ${куда}; ждут отправки заказа. ` + "Отправятся только после статуса заказа «отправлен».";
      if (state.status === "sent")
        title = `Техкарты собраны ${куда}; заказ уже отправлен. Ожидается постановка техкарт в очередь.`;
      else if (state.status === "unverified")
        title += " Заказ не подтверждён. Проверьте чат перед повтором заказа ↻.";
      else if (!isBusy(state)) title += " Заказ не отправлен. Повтор заказа ↻ соберёт техкарты заново.";
      return { text: "Техкарты: ждут", tone: "muted", title: `${title}\n${пачки}` };
    }
    case "sending":
      return {
        text: "Техкарты: отправка",
        tone: "muted",
        title: `Техкарты отправляются ${куда} через очередь WeChat.\n${пачки}`,
      };
    case "sent":
      return { text: "Техкарты ✓", tone: "ok", title: `Техкарты отправлены ${куда} и увидены в чате.\n${пачки}` };
    case "error":
      return {
        text: "Техкарты: ошибка",
        tone: "err",
        title:
          `Ошибка отправки техкарт ${куда}. ` +
          (state.status === "sent" ? "Заказ уже отправлен." : "Состояние заказа показано отдельно.") +
          `\n${пачки}\nПричина: ${tc.error?.trim() || "причина не названа"}.` +
          "\nЧасть техкарт могла уйти. Проверьте чат перед повтором заказа ↻: повтор отправит заказ заново.",
      };
  }
}

/** Пачка из полосы выбора (разметка 28.09.26 12:45 п.1): как сервер. */
export const MAX_BATCH = 20;
/** Темп очереди wechat-api — больше в час она не шлёт. */
export const RATE_PER_HOUR = 15;

export type BatchItem = {
  qr_id: number;
  outcome: "queued" | "busy" | "before" | "error";
  state: WechatState | null;
  error: { code: string; detail: string } | null;
};

/** Итог пачки одной строкой; ошибка хоть одной папки красит строку красным. */
export function batchNote(items: BatchItem[]): { text: string; error: boolean } {
  const count = (outcome: BatchItem["outcome"]) => items.filter((i) => i.outcome === outcome).length;
  const parts = [`WeChat: поставлено ${count("queued")}`];
  if (count("before")) parts.push(`уже отправляли ${count("before")}`);
  if (count("busy")) parts.push(`ещё отправляются ${count("busy")}`);
  const failed = items.filter((i) => i.outcome === "error");
  for (const i of failed) parts.push(`bqd-${i.qr_id}: ${i.error?.detail ?? "не отправлено"}`);
  return { text: parts.join(" · "), error: failed.length > 0 };
}

/**
 * Последняя отправка каждой папки. Опрашивает сервер, пока хоть одна идёт;
 * `put` кладёт ответ кнопки сразу, не дожидаясь опроса.
 */
export function useWechatStates(qrIds: number[]): {
  states: Record<number, WechatState>;
  put: (state: WechatState) => void;
  reload: () => void;
} {
  const [states, setStates] = useState<Record<number, WechatState>>({});
  const ids = qrIds.join(",");
  // Номер опроса: ответ на прежний набор папок не перетирает метки нового.
  const номер = useRef(0);

  const load = useCallback(async () => {
    if (!ids) return;
    const мой = ++номер.current;
    try {
      const got = await apiFetch<Record<string, WechatState>>(`/api/plans/wechat?qr=${ids}`);
      if (мой !== номер.current) return;
      // Слияние по папке, а не замена целиком: метки папок, ушедших из отбора,
      // остаются к их возвращению, а ответ кнопки (`put`) не теряется.
      setStates((prev) => ({ ...prev, ...Object.fromEntries(Object.values(got).map((s) => [s.qr_id, s])) }));
    } catch {
      // Метка — справка, а не условие работы экрана: следующий опрос попробует снова.
    }
  }, [ids]);

  useEffect(() => {
    void load();
  }, [load]);

  const busy = Object.values(states).some((s) => isBusy(s) || isTechcardPending(s));
  useEffect(() => {
    if (!busy) return;
    const timer = window.setInterval(() => void load(), POLL_MS);
    return () => window.clearInterval(timer);
  }, [busy, load]);

  const put = useCallback((state: WechatState) => {
    setStates((prev) => ({ ...prev, [state.qr_id]: state }));
  }, []);

  const reload = useCallback(() => void load(), [load]);

  return { states, put, reload };
}
